import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { IconAlertTriangle } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { Toggle } from '@/components/forms/Checkbox'
import { toast } from '@/components/toasts/toast.helpers'
import {
  createEntry,
  reorderEntry,
  reorderChecklistCategoriesAction,
  moveChecklistItemAction,
  createChecklistCategory,
  deleteChecklistCategory,
  updateChecklistCategoryName,
  toggleNovelChecklistItems,
} from '@/reducers/playbooks/playbook.redux'
import {
  fetchChecklistVariations,
  fetchPlaybookSectionWarnings,
} from '@/reducers/playbooks/playbook.actions'
import { Banner } from '@/components/banners/Banner'
import { reorder } from '@/utils/helpers'

import { accessorSectionMap, accessors } from '../../playbook.helpers'
import { SectionWarnings } from '../../components/SectionWarnings'
import { PlaybookEntry } from '../../components/PlaybookEntry'
import { PlaybookInlineEntry } from '../../components/PlaybookInlineEntry'
import { NovelContentView } from '../../components/NovelContentView'
import { CategoriesSidebarDisplay } from '../categories/CategoriesSidebarDisplay'
import { CategoryEntryDisplay } from '../categories/CategoryEntryDisplay'
import { CategoryHeader } from '../categories/CategoryHeader'
import { checklistToCategoryFormat, UNCATEGORIZED_ID } from './checklist.helpers'

export const NestedChecklistDisplay = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { section, id } = useParams()
  const {
    playbook,
    playbook: { current: isLatestPlaybookVersion, active },
    warnings,
    loading,
    checklistVariations,
    novelChecklistItemsVisible,
    novelChecklistItems,
  } = useSelector((state) => state.playbook)
  const { edit_config } = useSelector((state) => state.currentUser)
  const accessor = accessorSectionMap[section]
  const checklist = playbook.body[accessor]
  const readOnly = !isLatestPlaybookVersion || !edit_config
  const showChecklistVariationBanner = !readOnly && !active

  const categories = useMemo(() => checklistToCategoryFormat(checklist), [checklist])

  const handleToggleNovelContent = () => {
    dispatch(toggleNovelChecklistItems(!novelChecklistItemsVisible))
  }

  useEffect(() => {
    if (playbook.cid) {
      dispatch(fetchChecklistVariations(playbook.cid, playbook.id))
    }
  }, [playbook.cid, active])

  const handleSidebarReorder = (newOrder) => {
    dispatch(reorderChecklistCategoriesAction(newOrder))
  }

  const handleDragEnd = (result) => {
    const { type, source, destination } = result

    if (!destination) {
      return
    }

    if (type === 'droppable-checklist-category') {
      const namedOrder = categories.order.filter((catId) => catId !== UNCATEGORIZED_ID)
      const newOrder = reorder(namedOrder, source.index, destination.index)
      dispatch(reorderChecklistCategoriesAction(newOrder))
    }

    if (type === 'droppable-checklist-item') {
      const sourceCategoryId = source.droppableId
      const destinationCategoryId = destination.droppableId
      const sourceIsUncategorized = sourceCategoryId === UNCATEGORIZED_ID
      const destinationIsUncategorized = destinationCategoryId === UNCATEGORIZED_ID
      const realSourceCategoryId = sourceIsUncategorized ? null : sourceCategoryId
      const realDestinationCategoryId = destinationIsUncategorized ? null : destinationCategoryId

      const sourceGroup = categories.entries[sourceCategoryId]
      const destinationGroup = categories.entries[destinationCategoryId]

      if (!sourceGroup || !destinationGroup) {
        return
      }

      // If drag and dropping within the same category
      if (realSourceCategoryId === realDestinationCategoryId) {
        const reorderedIds = reorder(sourceGroup.order, source.index, destination.index)

        // Rebuild full flat order
        const newOrder = []
        for (const catId of categories.order) {
          if (catId === sourceCategoryId) {
            newOrder.push(...reorderedIds)
          } else {
            newOrder.push(...categories.entries[catId].order)
          }
        }

        if (realDestinationCategoryId) {
          const movedItemId = sourceGroup.order[source.index]
          dispatch(
            moveChecklistItemAction({
              entryId: movedItemId,
              targetCategoryId: realDestinationCategoryId,
              newOrder,
            })
          )
        } else {
          dispatch(reorderEntry(accessor)(newOrder))
        }
      } else {
        // If drag and dropping to a new category
        const movedItemId = sourceGroup.order[source.index]

        // Build new order by walking categories and inserting the item at the right position
        const newOrder = []
        for (const catId of categories.order) {
          const itemIds = categories.entries[catId].order.filter((itemId) => itemId !== movedItemId)

          if (catId === destinationCategoryId) {
            itemIds.splice(destination.index, 0, movedItemId)
          }

          newOrder.push(...itemIds)
        }

        dispatch(
          moveChecklistItemAction({
            entryId: movedItemId,
            targetCategoryId: realDestinationCategoryId,
            newOrder,
          })
        )
      }
    }
  }

  const handleDelete = (categoryId) => {
    dispatch(deleteChecklistCategory(categoryId))
    toast.success('Category has been deleted. Items moved to Uncategorized.')
    dispatch(fetchPlaybookSectionWarnings(accessor))
  }

  const handleRename = ({ name, categoryId }) => {
    dispatch(updateChecklistCategoryName({ name, categoryId }))
  }

  const handleCreateItem = (categoryId) => {
    const categoryParam =
      categoryId && categoryId !== UNCATEGORIZED_ID ? `?category_id=${categoryId}` : ''
    history.push(`/playbooks/${id}/${section}/create${categoryParam}`)
  }

  const renderCategoryHeader = (categoryId, category, isUncategorized) => {
    return (
      <CategoryHeader
        categoryId={categoryId}
        category={category}
        readOnly={readOnly}
        nestedEntriesCount={category.order.length}
        isUncategorized={isUncategorized}
        onDelete={handleDelete}
        onRename={handleRename}
        onCreateItem={handleCreateItem}
        deleteMessage={
          !isUncategorized
            ? `Are you sure you want to delete the category "${category.name}"? ${pluralize('item', category.order.length, true)} will be moved to Uncategorized.`
            : undefined
        }
        showEmptyWarning={false}
        editTestId="edit-checklist-category-name"
        addItemTestId="add-checklist-item-button"
      />
    )
  }

  const renderEntry = (entry, entryId) => {
    return (
      <PlaybookEntry
        accessor={accessors.CHECKLIST}
        entryId={entryId}
        entry={entry}
        isLockable
        isWinnable
        label="checklist item"
        readOnly={readOnly}
        checklistVariationCount={checklistVariations?.[entryId]}
      />
    )
  }

  const renderEmptyCategory = (categoryId, isUncategorized) => {
    return (
      <div className="muted-text padding">
        {isUncategorized ? 'No uncategorized items' : 'Drag items here or click Add Item'}
      </div>
    )
  }

  const renderCategoryFooter = (categoryId, isUncategorized) => {
    if (!isUncategorized || readOnly) {
      return null
    }

    return (
      <PlaybookInlineEntry
        createEntry={createEntry(accessors.CHECKLIST)}
        label="Add Item"
        type="entry"
      />
    )
  }

  return (
    <div className="playbook-category-and-entry-wrapper">
      <CategoriesSidebarDisplay
        categories={categories}
        readOnly={readOnly}
        onReorder={handleSidebarReorder}
        createAction={createChecklistCategory}
        uncategorizedId={UNCATEGORIZED_ID}
      />
      <div className="playbook-entries">
        {isLatestPlaybookVersion && (
          <SectionWarnings
            section={section}
            warnings={warnings}
            loading={loading.warnings}
            style={{ margin: '0px calc(30px + 0.75rem) 1rem calc(30px + 0.75rem)' }}
          />
        )}
        {showChecklistVariationBanner && (
          <Banner
            icon={<IconAlertTriangle />}
            informative
            style={{ margin: '0px calc(30px + 0.75rem) 1rem calc(30px + 0.75rem)', rowGap: 0 }}
          >
            Checklist variations are not visible while playbook is in draft mode.
          </Banner>
        )}
        <CategoryEntryDisplay
          categories={categories}
          readOnly={readOnly}
          onDragEnd={handleDragEnd}
          renderCategoryHeader={renderCategoryHeader}
          renderEntry={renderEntry}
          renderEmptyCategory={renderEmptyCategory}
          renderCategoryFooter={renderCategoryFooter}
          uncategorizedId={UNCATEGORIZED_ID}
          categoryDragType="droppable-checklist-category"
          entryDragType="droppable-checklist-item"
        />
        <div className="playbook-bottom-container">
          <div className="checklist-bottom-controls-container">
            <Toggle
              data-testid="novel-checklist-content-toggle"
              className="novel-checklist-content-toggle"
              checked={novelChecklistItemsVisible}
              onChange={handleToggleNovelContent}
              disabled={isEmpty(novelChecklistItems)}
              label={`Display Custom Items (${novelChecklistItems.length})`}
            />
          </div>
          {novelChecklistItemsVisible && (
            <NovelContentView novelChecklistItems={novelChecklistItems} />
          )}
        </div>
      </div>
    </div>
  )
}
